import React, { useEffect, useState } from 'react';
import { FiAlertTriangle, FiCheck, FiCheckSquare, FiStar, FiSun, FiX } from 'react-icons/fi';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import API from '../../utils/api';
import styles from './AdminResultDetail.module.css';

const ROLE_FULL = { RSM: 'Regional Sales Manager', FSM: 'Field Sales Manager', TSE: 'Territory Sales Manager' };
const SECTION_LABELS = { cognitive: 'Cognitive Skills', personality: 'Personality & EQ', leadership: 'Leadership', casestudy: 'Case Study' };
const SECTION_KEYS = ['cognitive', 'personality', 'leadership', 'casestudy'];
const SECTION_MAX = { cognitive: 100, personality: 100, leadership: 100, casestudy: 100 };

function getGradeColor(g) {
  return { A: '#1a5c38', B: '#27ae60', C: '#e67e22', D: '#e67e22', F: '#c0392b' }[g] || '#6b6b6b';
}

function BarMeter({ value, color }) {
  return (
    <div className={styles.barTrack}>
      <div className={styles.barFill} style={{ width: `${Math.min(value || 0, 100)}%`, background: color || '#1a5c38' }} />
    </div>
  );
}

function formatTimeSpent(startedAt, completedAt) {
  if (!startedAt || !completedAt) return 'N/A';
  const start = new Date(startedAt);
  const end = new Date(completedAt);
  const diffMs = end - start;
  const diffSecs = Math.floor(diffMs / 1000);
  
  const hours = Math.floor(diffSecs / 3600);
  const minutes = Math.floor((diffSecs % 3600) / 60);
  const seconds = diffSecs % 60;
  
  if (hours > 0) {
    return `${hours}h ${minutes}m ${seconds}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  } else {
    return `${seconds}s`;
  }
}

export default function AdminResultDetail() {
  const { idOrSlug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get(`/admin/candidates/${idOrSlug}`)
      .then(res => setData(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [idOrSlug]);

  if (loading) return <AdminLayout><div className={styles.loading}>Loading report...</div></AdminLayout>;
  if (!data?.assessment) return (
    <AdminLayout>
      <div className={styles.noResult}>
        <button className={styles.backBtn} onClick={() => navigate(-1)}>← Back</button>
        <p>No assessment result found for this candidate.</p>
      </div>
    </AdminLayout>
  );

  const { candidate, assessment, aiInsights } = data;
  const a = assessment;
  const getSection = key => a.sections?.find(s => s.section === key);

  // Use AI insights if available, otherwise use basic data
  const rec = aiInsights?.recommendation?.decision || a.recommendation;
  const recLabel = { advance: 'Advance to Next Stage', hold: 'Hold — Reassess in 6 Months', reject: 'Do Not Advance at This Time' }[rec];
  const recDesc = aiInsights?.recommendation?.rationale || {
    advance: `${candidate.fullName}'s performance profile meets the threshold for this role.`,
    hold: `${candidate.fullName}'s profile partially meets the threshold. Targeted development is recommended.`,
    reject: `${candidate.fullName}'s current performance profile does not meet the minimum threshold for this role. Reassessment may be considered after a 6-month development period.`
  }[rec];

  const cp = a.competencyProfile || {};
  
  // Declare sections early so they can be used below
  const cog = getSection('cognitive');
  const per = getSection('personality');
  const lea = getSection('leadership');
  const cas = getSection('casestudy');
  
  // Section 2 (Personality) Guidance
  const section2Score = per?.percentage || 0;
  const section2Guidance = (() => {
    if (section2Score >= 80) {
      return {
        level: 'Strong',
        color: '#1a5c38',
        title: 'Strong Self-Reported Behavioral Alignment',
        description: `With a Section 2 score of ${section2Score}%, this candidate demonstrates strong self-reported behavioral alignment with the role profile. Their personality and emotional intelligence responses indicate excellent fit for the position.`,
        interpretation: 'This score suggests the candidate has strong self-awareness and reports behaviors that closely align with role expectations.'
      };
    } else if (section2Score >= 65) {
      return {
        level: 'Moderate',
        color: '#e67e22',
        title: 'Moderate Self-Reported Behavioral Alignment',
        description: `With a Section 2 score of ${section2Score}%, this candidate demonstrates moderate alignment with some developmental gaps. Their personality and emotional intelligence responses indicate reasonable fit with areas for development.`,
        interpretation: 'This score suggests moderate behavioral alignment. Targeted development in specific competency areas is recommended.'
      };
    } else {
      return {
        level: 'Below Alignment',
        color: '#c0392b',
        title: 'Limited Self-Reported Behavioral Alignment',
        description: `With a Section 2 score of ${section2Score}%, this candidate's self-reported behaviors do not align well with the role profile. Significant development or reassessment may be needed.`,
        interpretation: 'This score indicates that self-reported behavioral patterns may not be well-suited for the role. Consider further assessment or development planning.'
      };
    }
  })();

  const compItems = [
    { label: 'Achievement Drive', value: cp.achievementDrive },
    { label: 'Resilience & Adaptability', value: cp.resilienceAdaptability },
    { label: 'Empathy & Interpersonal', value: cp.empathyInterpersonal },
    { label: 'Team Leadership', value: cp.teamLeadership },
    { label: 'Analytical Thinking', value: cp.analyticalThinking },
    { label: 'Initiative & Drive', value: cp.initiativeDrive },
    { label: 'Integrity & Ethics', value: cp.integrityEthics },
    { label: 'Strategic Orientation', value: cp.strategicOrientation },
  ];

  // Use AI observations if available, otherwise generate fallback observations
  const observations = aiInsights?.observations || [];
  
  // Fallback observations if AI didn't generate any
  if (observations.length === 0) {
    if (cog && cog.percentage < 60) observations.push({ type: 'concern', text: `Cognitive score below threshold (${cog.percentage}%). Consider targeted assessment in numerical reasoning and logical problem-solving.` });
    if (cas && cas.percentage < 40) observations.push({ type: 'concern', text: `Case study performance (${cas.rawScore}/100) suggests limited proficiency in sales data interpretation and performance gap analysis at this time.` });
    const highestComp = compItems.reduce((a, b) => (b.value > a.value ? b : a), compItems[0]);
    if (highestComp) observations.push({ type: 'strength', text: `Highest competency: ${highestComp.label} (${highestComp.value}%). This is a clear strength to be leveraged in the role.` });
    const lowestComp = compItems.reduce((a, b) => (b.value < a.value ? b : a), compItems[0]);
    if (lowestComp && lowestComp.label !== highestComp.label) observations.push({ type: 'development', text: `Development opportunity: ${lowestComp.label} (${lowestComp.value}%). Recommend targeted coaching and development in this area.` });
  }

  const roleLevelCalibration = aiInsights?.roleLevelCalibration || 
    `This assessment is calibrated for a senior management role requiring strategic thinking, commercial acumen, and organizational leadership. The ${candidate.role} assessment is set at a higher cognitive and strategic complexity level.`;

  return (
    <AdminLayout>
      <div className={styles.page}>
        <div className={styles.topBar}>
          <button className={styles.backBtn} onClick={() => navigate(-1)}>← Back to Results</button>
          <button className={styles.printBtn} onClick={() => window.print()}>⎙ Print Report</button>
        </div>

        {/* Header Card */}
        <div className={styles.headerCard}>
          <div className={styles.candidateInfo}>
            {candidate.candidatePhoto && (
              <img 
                src={candidate.candidatePhoto} 
                alt={candidate.fullName} 
                style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover', marginRight: 16 }}
              />
            )}
            <div>
              <h2 className={styles.candidateName}>{candidate.fullName}</h2>
              <p className={styles.candidateEmail}>{candidate.email}</p>
            </div>
          </div>
          <div className={styles.headerRight}>
            <p className={styles.assessDate}>Assessment Date: {new Date(a.submittedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}</p>
            <span className={styles.roleTag}>{candidate.role} — {ROLE_FULL[candidate.role]}</span>
            <p className={styles.timeSpent}>Time Spent: {formatTimeSpent(candidate.assessmentStarted, candidate.assessmentCompleted)}</p>
          </div>
        </div>

        {/* Recommendation Banner */}
        <div className={`${styles.recBanner} ${rec === 'advance' ? styles.recGreen : rec === 'hold' ? styles.recOrange : styles.recRed}`}>
          <span className={styles.recIcon}>{rec === 'advance' ? <FiCheck aria-hidden="true" /> : <FiX aria-hidden="true" />}</span>
          <div>
            <div className={styles.recTitle}>Recommendation: {recLabel}</div>
            <div className={styles.recDesc}>{recDesc}</div>
          </div>
        </div>

        {/* Overall Score */}
        <div className={styles.overallCard}>
          <p className={styles.overallLabel}>OVERALL ASSESSMENT SCORE</p>
          <div className={styles.overallScore}>{a.totalScore}<span className={styles.overallMax}>/400</span></div>
          <div className={styles.overallPct} style={{ color: getGradeColor(a.overallGrade) }}>
            {a.totalPercentage}% — {a.totalPercentage >= 70 ? 'Advance Threshold (70%+)' : a.totalPercentage >= 60 ? 'Conditional Review (60-69%)' : 'Below Threshold (<60%)'}
          </div>
        </div>

        {/* Section Cards */}
        <div className={styles.sectionScores}>
          {[
            { key: 'cognitive', sec: cog, label: 'Cognitive & Analytical' },
            { key: 'personality', sec: per, label: 'Personality & EQ' },
            { key: 'leadership', sec: lea, label: 'Leadership & Team Mgmt' },
            { key: 'casestudy', sec: cas, label: 'Technical Case Study' },
          ].map(({ key, sec, label }) => (
            <div key={key} className={styles.secCard}>
              <div className={styles.secName}>{label}</div>
              <div className={styles.secScore}>
                <span className={styles.secNum}>{sec?.rawScore ?? '—'}</span>
                <span className={styles.secMax}>/100</span>
              </div>
              <div className={styles.secBar}>
                <div className={styles.secBarFill} style={{
                  width: `${sec?.percentage || 0}%`,
                  background: sec?.grade === 'A' ? '#1a5c38' : sec?.grade === 'B' ? '#27ae60' : sec?.grade === 'C' ? '#e67e22' : sec?.grade === 'D' ? '#e67e22' : '#c0392b'
                }} />
              </div>
              <div className={styles.secGrade} style={{ color: getGradeColor(sec?.grade) }}>
                {sec?.percentage ?? 0}% — Grade {sec?.grade ?? 'N/A'}
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Section: Section 2 Guidance + Score Breakdown */}
        <div className={styles.bottomGrid}>
          {/* Section 2 Guidance */}
          <div className={styles.compCard}>
            <div className={styles.cardTitle}>
              <span>Personality & EQ Assessment (Section 2)</span>
              <span style={{ fontSize: '0.9rem', fontWeight: '600', color: section2Guidance.color }}>
                {section2Score >= 80 ? '80–100: Strong' : section2Score >= 65 ? '65–79: Moderate' : 'Below 65: Limited'}
              </span>
            </div>
            
            <div style={{ marginBottom: '1rem' }}>
              <div style={{ 
                padding: '1rem', 
                background: section2Guidance.color + '15', 
                border: `2px solid ${section2Guidance.color}`,
                borderRadius: '8px',
                marginBottom: '1rem'
              }}>
                <div style={{ fontSize: '1.1rem', fontWeight: '700', color: section2Guidance.color, marginBottom: '0.5rem' }}>
                  {section2Guidance.title}
                </div>
                <div style={{ fontSize: '0.9rem', color: 'var(--gray-700)', lineHeight: '1.6', marginBottom: '0.75rem' }}>
                  {section2Guidance.description}
                </div>
                <div style={{ fontSize: '0.85rem', color: 'var(--gray-600)', fontStyle: 'italic', borderTop: '1px solid' + section2Guidance.color + '30', paddingTop: '0.75rem' }}>
                  <FiSun aria-hidden="true" /> <strong>Interpretation:</strong> {section2Guidance.interpretation}
                </div>
              </div>
            </div>

            <div style={{ marginTop: '1.5rem' }}>
              <div style={{ fontSize: '0.9rem', fontWeight: '600', color: 'var(--gray-900)', marginBottom: '1rem' }}>
                Competency Profile (Section 2)
              </div>
              <div className={styles.compList}>
                {compItems.map((item, idx) => (
                  <div key={idx} className={styles.compItem}>
                    <div className={styles.compLabel}>{item.label}</div>
                    <div className={styles.compBarWrap}>
                      <div className={styles.compBar} style={{ width: `${Math.min(item.value || 0, 100)}%` }} />
                    </div>
                    <div className={styles.compValue}>{item.value || 0}%</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Score Breakdown */}
          <div className={styles.breakdownCard}>
            <div className={styles.cardTitle}>Score Breakdown</div>
            <table className={styles.breakTable}>
              <thead>
                <tr><th>S/N</th><th>Section</th><th>Score</th><th>%</th><th>Grade</th></tr>
              </thead>
              <tbody>
                {[
                  { label: '1 — Cognitive Skills', sec: cog },
                  { label: '2 — Personality & EQ', sec: per },
                  { label: '3 — Leadership', sec: lea },
                  { label: '4 — Case Study', sec: cas },
                ].map(({ label, sec }, index) => (
                  <tr key={label}>
                    <td>{index + 1}</td>
                    <td>{label}</td>
                    <td style={{ fontWeight: 500 }}>{sec?.rawScore ?? '—'}/100</td>
                    <td style={{ color: getGradeColor(sec?.grade), fontWeight: 500 }}>{sec?.percentage ?? '—'}%</td>
                    <td style={{ fontWeight: 700, color: getGradeColor(sec?.grade) }}>{sec?.grade ?? '—'}</td>
                  </tr>
                ))}
                <tr className={styles.totalRow}>
                  <td>—</td>
                  <td style={{ fontWeight: 600 }}>Total</td>
                  <td style={{ fontWeight: 600 }}>{a.totalScore}/400</td>
                  <td style={{ fontWeight: 600, color: getGradeColor(a.overallGrade) }}>{a.totalPercentage}%</td>
                  <td style={{ fontWeight: 700, color: getGradeColor(a.overallGrade) }}>{a.overallGrade}</td>
                </tr>
              </tbody>
            </table>

            <div className={styles.calibBox}>
              <div className={styles.calibTitle}>Role Level Calibration</div>
              <div className={styles.calibText}>
                {roleLevelCalibration}
              </div>
            </div>
          </div>
        </div>

        {/* Assessor Observations */}
        {observations.length > 0 && (
          <div className={styles.obsCard}>
            <div className={styles.cardTitle}>Assessor Observations</div>
            <div className={styles.obsList}>
              {observations.map((o, i) => (
                <div key={i} className={styles.obsItem}>
                  <span className={styles.obsIcon}>
                    {o.type === 'strength'
                      ? <FiStar aria-hidden="true" />
                      : o.type === 'concern'
                        ? <FiAlertTriangle aria-hidden="true" />
                        : <FiCheckSquare aria-hidden="true" />}
                  </span>
                  <span className={styles.obsText}>{o.text}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* View More Button */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.5rem' }}>
          <button 
            onClick={() => navigate(`/admin/results/${idOrSlug}/insights`)}
            style={{
              padding: '0.7rem 1.8rem',
              background: 'var(--green)',
              color: 'white',
              border: 'none',
              borderRadius: 'var(--radius-lg)',
              fontSize: '0.9rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s',
              boxShadow: '0 4px 12px rgba(26, 92, 56, 0.2)'
            }}
            onMouseEnter={e => e.target.style.background = '#2d8659'}
            onMouseLeave={e => e.target.style.background = 'var(--green)'}
          >
            View More Insights →
          </button>
        </div>

        {/* Footer */}
        <div className={styles.reportFooter}>
          <div className={styles.footerContent}>
            <div className={styles.footerCopyright}>
              © 2026 UAC Foods Limited. This assessment report is confidential and the sole property of UAC Foods Limited. It must not be shared, reproduced, or used outside the organization without express written authorization from UAC Foods HR Leadership. This tool has been developed exclusively for UAC Foods internal talent acquisition and is protected under applicable intellectual property laws.
            </div>
            <div className={styles.footerMeta}>
              <span>Report generated: {new Date(a.submittedAt).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}, {new Date(a.submittedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>
              <span className={styles.footerDivider}>|</span>
              <span>Assessment System v1.0</span>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
